import { DatePickerButton } from "@/components/DatePickerButton";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Repeat } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { useApi, errMsg, ago, siteToday } from "@/lib/api";

export const BetfairPanel = () => {
  const api = useApi();
  const [d, setD] = useState(null);
  const [date, setDate] = useState(siteToday());
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api("get", "/admin/betfair").then(setD).catch(() => {}), [api]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api("get", "/meetings/dates").then((ds) => { const t = siteToday(); if (ds?.length && !ds.includes(t)) setDate(ds.find((x) => x > t) ? [...ds].reverse().find((x) => x > t) : ds[0]); }).catch(() => {});
  }, [api]);
  if (!d) return null;

  const sync = async () => {
    setBusy(true);
    try {
      const { id } = await api("post", "/admin/betfair/sync", { date });
      for (let i = 0; i < 40; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const j = await api("get", `/admin/jobs/${id}`);
        if (j.state === "error") throw new Error(j.error);
        if (j.state === "done") {
          const races = (j.results || []).reduce((n, x) => n + (x.races || 0), 0);
          const miss = (j.results || []).filter((x) => !x.matched).map((x) => x.track);
          toast.success(`Betfair prices & silks added to ${races} race(s)${miss.length ? `. No Betfair markets yet for: ${miss.join(", ")}` : ""}`);
          break;
        }
      }
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false); load();
  };
  const toggle = async (auto) => {
    try { await api("put", "/admin/betfair", { auto }); toast.success(auto ? "Betfair will refresh hourly 7am–5pm" : "Automatic Betfair refresh off"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <section className="rounded-2xl bg-[#131C31] border border-white/5 p-5 space-y-3" data-testid="admin-betfair-panel">
      <h2 className="font-display text-lg font-bold flex items-center gap-2"><Repeat className="w-4 h-4 text-amber-400" />Exchange prices & silks · Betfair</h2>
      <p className="text-xs text-slate-500" data-testid="betfair-status">
        {d.configured ? "Connected through your Australian relay" : `Not set up yet (missing: ${d.missing.join(", ")})`} · last update {d.last_sync ? ago(d.last_sync) : "never"}
      </p>
      <p className="text-[11px] text-slate-500">Betfair prices and silks are shown first; Krok and your typed prices fill any gaps.</p>
      <div className="flex flex-wrap items-center gap-2">
        <DatePickerButton testid="betfair-date-input" value={date} onChange={setDate} className="bg-[#0B101D] border border-white/10 rounded-full px-3 py-1.5 text-xs text-slate-100 hover:border-blue-500/50 transition-colors" />
        <button data-testid="betfair-sync-button" onClick={sync} disabled={busy || !d.configured} className="px-4 py-1.5 rounded-full bg-amber-500 hover:bg-amber-400 text-slate-950 text-xs font-semibold disabled:opacity-50">{busy ? "Updating…" : "Update Betfair prices"}</button>
      </div>
      <label className="flex items-center gap-3 text-sm">
        <span className="mr-auto">Refresh automatically every hour (7am–5pm)<span className="block text-[11px] text-slate-500">Free — Betfair doesn't charge per request.</span></span>
        <Switch data-testid="betfair-auto-toggle" checked={d.auto} onCheckedChange={toggle} disabled={!d.configured} />
      </label>
      {d.last_job?.state === "error" && <p className="text-xs text-rose-300" data-testid="betfair-error">{d.last_job.error}</p>}
    </section>
  );
};
